import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API } from '../../../../config/api';
import { accountingApi } from '../../../../config/apiClient';
import { useAuth } from '../../../../contexts/AuthContext';
import AccountingDataTable from '../../shared/AccountingDataTable';
import type { DataTableColumn } from '../../shared/AccountingDataTable';

interface PaymentRow {
  id: number;
  bankLoanId: number;
  installmentId: number;
  loanName: string;
  installmentNo: number;
  installmentAmount: number;
  lateInterestAmount: number;
  totalPaid: number;
  paymentSource: string;
  cashboxId?: number | null;
  cashboxName?: string | null;
  bankAccountId?: number | null;
  bankAccountName?: string | null;
  paymentDate: string;
  description?: string | null;
  paidBy?: string | null;
  currency: string;
}

const LoanPayments: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  // Liste state'leri (server-side pagination)
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtreler
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loanFilter, setLoanFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loanOptions, setLoanOptions] = useState<any[]>([]);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Silme + snackbar
  const [deleteTarget, setDeleteTarget] = useState<PaymentRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await accountingApi.get(
        ACCOUNTING_API.BANK_LOAN_PAYMENTS({
          page: page + 1,
          pageSize,
          loanId: loanFilter ? parseInt(loanFilter, 10) : undefined,
          search: search || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        })
      );
      setPayments(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, loanFilter, search, dateFrom, dateTo]);

  const loadLoanOptions = useCallback(async () => {
    try {
      const res = await accountingApi.get(ACCOUNTING_API.BANK_LOANS({ page: 1, pageSize: 1000 }));
      setLoanOptions(res.data?.data || []);
    } catch (err) {
      console.error('[LoanPayments] Kredi listesi yüklenemedi:', err);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    loadLoanOptions();
  }, [loadLoanOptions]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(0);
      setSearch(value.trim());
    }, 500);
  };

  const fmt = (n: number, cur: string) =>
    `${Number(n || 0).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === 'TRY' ? '₺' : cur}`;

  const sourceChip = (row: PaymentRow) => {
    if (row.paymentSource === 'cashbox') {
      return <Chip size="small" color="warning" variant="outlined" label={`${tt('Kasa', 'Cashbox')}: ${row.cashboxName || '-'}`} />;
    }
    if (row.paymentSource === 'bank') {
      return <Chip size="small" color="info" variant="outlined" label={`${tt('Banka', 'Bank')}: ${row.bankAccountName || '-'}`} />;
    }
    return <Chip size="small" variant="outlined" label={tt('Tahakkuk (Elden)', 'Accrual (By hand)')} />;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accountingApi.post(ACCOUNTING_API.BANK_LOAN_PAYMENT_DELETE, {
        PaymentId: deleteTarget.id,
        DeletedBy: userDetails?.email || 'Sistem',
      });
      setDeleteTarget(null);
      setSnackbar(tt('Ödeme silindi; taksit tekrar bekliyor durumuna alındı', 'Payment deleted; installment returned to pending'));
      loadPayments();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<DataTableColumn<PaymentRow>[]>(() => [
    {
      id: 'paymentDate',
      label: tt('Ödeme Tarihi', 'Payment Date'),
      render: (row) => <>{new Date(row.paymentDate).toLocaleDateString(localeCode)}</>,
    },
    {
      id: 'loanName',
      label: tt('Kredi', 'Loan'),
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.loanName}</Typography>
      ),
    },
    {
      id: 'installmentNo',
      label: tt('Taksit No', 'Installment No'),
      align: 'center',
      render: (row) => <>{row.installmentNo}</>,
    },
    {
      id: 'installmentAmount',
      label: tt('Taksit Tutarı', 'Installment Amount'),
      align: 'right',
      render: (row) => <>{fmt(row.installmentAmount, row.currency)}</>,
    },
    {
      id: 'lateInterest',
      label: tt('Gecikme Faizi', 'Late Interest'),
      align: 'right',
      render: (row) =>
        Number(row.lateInterestAmount || 0) > 0 ? (
          <Chip size="small" color="warning" label={fmt(row.lateInterestAmount, row.currency)} />
        ) : (
          <>-</>
        ),
    },
    {
      id: 'totalPaid',
      label: tt('Toplam Ödenen', 'Total Paid'),
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="success.main">
          {fmt(row.totalPaid, row.currency)}
        </Typography>
      ),
    },
    {
      id: 'source',
      label: tt('Kaynak', 'Source'),
      render: (row) => sourceChip(row),
    },
    {
      id: 'paidBy',
      label: tt('Ödeyen', 'Paid By'),
      render: (row) => <>{row.paidBy || '-'}</>,
    },
    {
      id: 'actions',
      label: tt('İşlem', 'Action'),
      align: 'right',
      render: (row) => (
        <Tooltip title={tt('Ödemeyi kalıcı olarak sil', 'Permanently delete payment')}>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [localeCode]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Başlık */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/krediler')} aria-label={tt('Geri', 'Back')}>
          <ArrowBackIcon />
        </IconButton>
        <PaymentsIcon color="success" sx={{ fontSize: 32 }} />
        <Typography variant="h5" component="h1" fontWeight="bold">
          {tt('Kredi Ödemeleri', 'Loan Payments')}
        </Typography>
      </Box>

      {/* Filtreler */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Kredi adı veya açıklama ara...', 'Search loan name or description...')}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="loan-payment-loan-filter">{tt('Kredi', 'Loan')}</InputLabel>
              <Select
                labelId="loan-payment-loan-filter"
                value={loanFilter}
                label={tt('Kredi', 'Loan')}
                onChange={(e) => { setLoanFilter(String(e.target.value)); setPage(0); }}
              >
                <MenuItem value="">{tt('Tümü', 'All')}</MenuItem>
                {loanOptions.map((l: any) => (
                  <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={tt('Başlangıç', 'From')}
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={tt('Bitiş', 'To')}
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadPayments}>
              {tt('Yenile', 'Refresh')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <AccountingDataTable<PaymentRow>
        columns={columns}
        rows={payments}
        getRowId={(row) => `loan-payment-${row.id}`}
        loading={loading}
        serverSidePagination
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage, newPageSize) => {
          setPage(newPage);
          setPageSize(newPageSize);
        }}
        emptyMessage={tt('Kredi ödemesi bulunamadı', 'No loan payments found')}
        emptyIcon={<PaymentsIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel={tt('Kredi ödemeleri tablosu', 'Loan payments table')}
        columnResizeStorageKey="bank-loan-payments"
      />

      {/* Silme (HARD DELETE) onayı */}
      <Dialog open={Boolean(deleteTarget)} onClose={deleting ? undefined : () => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Ödemeyi Sil', 'Delete Payment')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tt(
              `"${deleteTarget?.loanName}" — Taksit ${deleteTarget?.installmentNo} ödemesi kalıcı olarak silinecek (geri alınamaz). Kasadan/bankadan yapıldıysa ilgili kasa/banka hareketi de silinecek ve bakiye geri düzeltilecek. Taksit tekrar "Bekliyor" durumuna dönecek.`,
              `"${deleteTarget?.loanName}" — Payment of installment ${deleteTarget?.installmentNo} will be permanently deleted (cannot be undone). If it was paid from a cashbox/bank, the related transaction will also be deleted and the balance restored. The installment will return to "Pending".`
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>{tt('İptal', 'Cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {tt('Kalıcı Olarak Sil', 'Delete Permanently')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar(null)}>{snackbar}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LoanPayments;
